import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import {
  ContentCopy,
  Download,
  ExpandLess,
  ExpandMore,
  Publish,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { useSnackbar } from 'notistack';

import type {
  PatientResolution,
  SessionPatientMatchSchema,
} from '../store/hooks';
import {
  useBrowseStagedResourcesQuery,
  useCommitStagedResourcesMutation,
  useGetClassifySummaryQuery,
  useGetStagedResourceDocumentQuery,
  useGetStagedResourcesSummaryQuery,
  useImportOperationTracking,
  useListAllClassifyConflictsQuery,
  useListAllSessionPatientMatchesQuery,
  useListSourceProvidersQuery,
  useListStagedResourcesQuery,
  useRequestClassifyMutation,
} from '../store/hooks';
import { useDebounce } from '@hooks/useDebounce';

import { ArtifactViewerModal } from './artifact-viewer-modal';
import { CommitBoard } from './commit-board';
import type { ReviewResourceRow } from './import-review-browser';
import { appointmentDescriptor, ImportReviewBrowser } from './import-review-browser';
import type {
  ImportVerdict,
  ReviewDecision,
  ReviewMatchCandidate,
  ReviewMatchResolution,
} from './import-review-types';
import { ProviderMappingPanel } from './provider-mapping-panel';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

/** Filename of an artifact relative to its `_artifacts/` prefix. */
const artifactName = (key: string, prefix?: string): string =>
  prefix && key.startsWith(prefix) ? key.slice(prefix.length) : key;

const STATUSES = ['staged', 'invalid', 'committed', 'skipped'] as const;
const PER_PAGE = 25;
// Review-browser page size. Browsing is server-driven (type filter, search,
// and paging all query the server — GDN-2244), so this only sets how many
// decrypted rows load per page; every conflict and patient match is still
// fully loaded separately, so nothing that blocks a commit is ever hidden.
const BROWSE_PAGE_SIZE = 50;

/** Best-effort human label for a converted payload (mirrors the BE). */
function reviewLabel(
  data: Record<string, unknown> | undefined,
  sourceKey: string,
  resourceType?: string,
): string {
  const d = data ?? {};
  const str = (k: string): string => (typeof d[k] === 'string' ? (d[k] as string) : '');
  const first = str('first_name');
  const last = str('last_name');
  if (first || last) return [first, last].filter(Boolean).join(' ');
  for (const k of ['display_name', 'name', 'title']) {
    const v = d[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  // Type-aware fallbacks for payloads with no name-ish field — a raw source
  // key tells the operator nothing (GDN-2211).
  if (resourceType === 'encounter') {
    const kind = (str('encounter_type') || 'encounter').replaceAll('_', ' ');
    const when = (str('start_time') || str('scheduled_for')).slice(0, 10);
    return [kind, when].filter(Boolean).join(' · ');
  }
  if (resourceType === 'patient_coverage' && str('payer_name')) {
    return str('payer_name');
  }
  if (resourceType === 'claim') {
    const [, claimNumber] = sourceKey.split(':', 2);
    return `Claim ${claimNumber ?? sourceKey}`;
  }
  return sourceKey;
}

const STATUS_COLOR: Record<
  string,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  staged: 'info',
  invalid: 'error',
  committed: 'success',
  skipped: 'warning',
};

interface StagedResourcesSectionProps {
  organizationId: string;
  sessionId: string;
  /** Offer the Commit action (the v2 Data Import batch detail enables it). */
  showCommit?: boolean;
}

/**
 * Displays the staged `import_resources` for an import session — a summary of
 * counts by resource type × status, plus a filterable, paginated table of the
 * staged resource envelopes (status, source key, validation errors, and the
 * converted resource data). The conversion that produces these runs outside
 * this repo. With `showCommit`, also offers the commit step: staged rows
 * become real records via an async operation polled to completion.
 */
export function StagedResourcesSection({
  organizationId,
  sessionId,
  showCommit = false,
}: StagedResourcesSectionProps) {
  const [page, setPage] = useState(1);
  const [resourceType, setResourceType] = useState('');
  const [status, setStatus] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  // source provider key -> platform user id, supplied by the operator.
  const [providerMapping, setProviderMapping] = useState<Record<string, string>>(
    {},
  );
  // Operator match decisions, keyed by patient source key. An ambiguous patient
  // included in the commit must be resolved here before commit is allowed.
  const [resolutions, setResolutions] = useState<Map<string, PatientResolution>>(
    new Map(),
  );
  const [viewerKey, setViewerKey] = useState<string | null>(null);
  const [provenanceOpen, setProvenanceOpen] = useState(false);
  // Source-document preview (GDN-2214): the staged row whose document is open.
  const [docViewId, setDocViewId] = useState<string | null>(null);
  const {
    data: docContent,
    isFetching: docLoading,
    isError: docError,
  } = useGetStagedResourceDocumentQuery(
    {
      organizationId,
      sessionId,
      importResourceId: docViewId ?? '',
    },
    { skip: !docViewId },
  );
  const { enqueueSnackbar } = useSnackbar();
  const copy = useCopyToClipboard();

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
    refetch: refetchSummary,
  } = useGetStagedResourcesSummaryQuery({ organizationId, sessionId });

  const {
    data: list,
    isLoading: listLoading,
    isFetching,
    refetch: refetchList,
  } = useListStagedResourcesQuery({
    organizationId,
    sessionId,
    page,
    perPage: PER_PAGE,
    resourceType: resourceType || undefined,
    status: status || undefined,
  });

  // Classify review (GDN-2178): a verdict-aware browser over every staged row.
  // Operator decisions for conflicting rows, keyed by import_resource_id. Sent
  // as conflict_resolutions; every in-scope conflict must be decided to commit.
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>(
    {},
  );
  const {
    data: classifySummary,
    isError: classifySummaryError,
    refetch: refetchClassifySummary,
  } = useGetClassifySummaryQuery({
    organizationId,
    sessionId,
  });
  const hasClassifyVerdicts = (classifySummary?.total_records ?? 0) > 0;
  // The field-merge detail (competing + fills) carries PHI — fetch it only when
  // the session has matched-with-changes rows (conflicts or updates) to enrich.
  const hasMatchedChanges =
    (classifySummary?.totals?.conflict ?? 0) > 0 ||
    (classifySummary?.totals?.update ?? 0) > 0;
  const {
    data: conflicts,
    isLoading: conflictsLoading,
    isError: conflictsError,
    refetch: refetchConflicts,
  } = useListAllClassifyConflictsQuery(
      // Fetch EVERY conflict in the session (paged to completion), not just the
      // first browse window. An undecided conflict is the only thing that blocks
      // a commit, so all of them must be shown and resolvable no matter how large
      // the import is; a conflict left off a page would block the commit with no
      // way to decide it. GDN-2198.
      { organizationId, sessionId },
      { skip: !hasMatchedChanges },
    );
  // Server-driven browse (GDN-2244): the type filter, search box, and paging
  // query the server, so they cover the WHOLE import — not a loaded window.
  const [browseType, setBrowseType] = useState<string>('all');
  // Two-surface page (GDN-2256): null = Overview (board hub); a type = the
  // Review surface scoped to that type, entered from a board row.
  const [openType, setOpenType] = useState<string | null>(null);
  const PROVIDERS_SURFACE = '__providers__';
  const openReview = (rt: string) => {
    setBrowseType(rt);
    setBrowsePage(1);
    setOpenType(rt);
  };
  const [browseSearch, setBrowseSearch] = useState('');
  const [browsePage, setBrowsePage] = useState(1);
  const debouncedSearch = useDebounce(browseSearch, 400);
  // New filter or search = new result set; always start it at page 1.
  useEffect(() => {
    setBrowsePage(1);
  }, [browseType, debouncedSearch]);
  const {
    data: browseData,
    isError: browseError,
    refetch: refetchBrowse,
  } = useBrowseStagedResourcesQuery(
    {
      organizationId,
      sessionId,
      page: browsePage,
      perPage: BROWSE_PAGE_SIZE,
      resourceType: browseType !== 'all' ? browseType : undefined,
      // Committed rows leave the queue — the review job is what's left.
      status: 'staged',
      search: debouncedSearch.trim() || undefined,
    },
    { skip: !hasClassifyVerdicts },
  );
  const allRowsData = browseData;

  // Review-surface verdict chips follow the type filter (GDN-2256 review
  // feedback): scoped to one type they show THAT type's lanes, not the
  // whole session's.
  const scopedClassifyCounts =
    browseType === 'all'
      ? classifySummary?.totals
      : classifySummary?.by_type?.find(
          (t) => t.resource_type === browseType,
        )?.counts;
  const browserTotals = {
    new: 0,
    unchanged: 0,
    update: 0,
    conflict: 0,
    ambiguous: 0,
    unprocessable: 0,
    ...(scopedClassifyCounts ?? {}),
  };
  const scopedTotalRecords =
    browseType === 'all'
      ? classifySummary?.total_records ?? 0
      : Object.values(scopedClassifyCounts ?? {}).reduce(
          (acc, n) => acc + (n ?? 0),
          0,
        );

  const browseTotalRows = allRowsData?.total ?? allRowsData?.items?.length ?? 0;
  // Whole-session totals from the summary (independent of any page).
  const totalConflictCount = classifySummary?.totals?.conflict ?? 0;
  // Pathological guard: the conflict fetch pages to completion, so this is only
  // true for an absurdly large conflict set (beyond the fetch's page backstop).
  // Gate the commit if we somehow couldn't load them all rather than miss one.
  const conflictsIncomplete =
    !!conflicts && (conflicts.total ?? 0) > (conflicts.items?.length ?? 0);

  // Per-patient match outcomes (staged patients only). Feeds the resolution UI
  // in the patient picker AND the ambiguous rows in the review browser — the
  // match feed carries candidate names/dob the staged row's persisted candidate
  // ids don't. Fetched to completion (paged past the 1000 cap) so a large batch
  // never silently hides an ambiguous match; matchesIncomplete still gates as a
  // backstop for the pathological case. GDN-2198 / GDN-2212.
  const {
    data: matchData,
    isLoading: matchesLoading,
    isError: matchesError,
    refetch: refetchMatches,
  } = useListAllSessionPatientMatchesQuery(
      { organizationId, sessionId },
      { skip: !showCommit },
    );
  const matchByKey = useMemo(
    () =>
      new Map<string, SessionPatientMatchSchema>(
        (matchData?.items ?? []).map(m => [m.patient_key, m]),
      ),
    [matchData],
  );
  const matchesIncomplete =
    !!matchData && (matchData.total ?? 0) > (matchData.items?.length ?? 0);
  // Patients the commit will silently link to existing records: correct when
  // the match is right, chart-corrupting when it isn't — say so up front.
  const autoLinkCount = useMemo(
    () =>
      [...matchByKey.values()].filter((m) => m.status === 'matched').length,
    [matchByKey],
  );
  const setResolution = useCallback(
    (key: string, resolution: PatientResolution | null) =>
      setResolutions(prev => {
        const next = new Map(prev);
        if (resolution) next.set(key, resolution);
        else next.delete(key);
        return next;
      }),
    [],
  );

  const reviewRows = useMemo<ReviewResourceRow[]>(() => {
    const items = allRowsData?.items ?? [];
    const conflictItems = conflicts?.items ?? [];
    const detailById = new Map(
      conflictItems.map((c) => [c.import_resource_id, c]),
    );
    const toDiff = (d: {
      field: string;
      existing?: string | null;
      incoming?: string | null;
    }) => ({ field: d.field, existing: d.existing ?? '', incoming: d.incoming ?? '' });
    // Candidates for an ambiguous patient row come from the match feed (which
    // carries names/dob for display); the staged row's persisted
    // match_candidates (ids + confidence only) are the fallback.
    const candidatesFor = (
      sourceKey: string,
      persisted: { patient_id: string; confidence: number; reason: string }[] | undefined,
    ): ReviewMatchCandidate[] | undefined => {
      const fromMatch = matchByKey.get(sourceKey)?.candidates;
      if (fromMatch && fromMatch.length > 0) {
        return fromMatch.map((c) => ({
          patientId: c.patient_id,
          label:
            [c.name, c.dob].filter(Boolean).join(' · ') || c.patient_id,
          confidence: c.confidence,
          reason: c.reason,
          identifier: c.identifier ?? undefined,
        }));
      }
      return persisted?.map((c) => ({
        patientId: c.patient_id,
        label: c.patient_id,
        confidence: c.confidence,
        reason: c.reason,
      }));
    };
    // Why a row is ambiguous — carried on the match feed (GDN-2244); read via
    // a cast until the generated client picks the field up.
    const basisFor = (sourceKey: string): string | undefined =>
      (matchByKey.get(sourceKey) as { match_basis?: string } | undefined)
        ?.match_basis ?? undefined;
    // Patient labels by source key, so child rows (encounters) can say whose
    // record they are. The match feed covers every staged patient regardless
    // of which browse page is loaded; page rows fill any gap (e.g. when the
    // match feed isn't fetched because commit isn't shown).
    const patientNameByKey = new Map<string, string>();
    for (const [key, m] of matchByKey) {
      if (m.name) patientNameByKey.set(key, m.name);
    }
    for (const i of items) {
      if (i.resource_type === 'patient' && !patientNameByKey.has(i.source_key)) {
        patientNameByKey.set(
          i.source_key,
          reviewLabel(i.data, i.source_key, 'patient'),
        );
      }
    }
    const windowedRows = items.flatMap((item) => {
      const classification = (item as { classification?: string | null })
        .classification;
      const verdict: ImportVerdict | undefined =
        item.status === 'invalid'
          ? 'unprocessable'
          : (classification as ImportVerdict | undefined) ?? undefined;
      if (!verdict) return [];
      const detail = detailById.get(item.import_resource_id);
      let label =
        detail?.title ??
        reviewLabel(item.data, item.source_key, item.resource_type);
      // Owning patient for a child row: prefer the server-resolved ref label
      // (present regardless of the browse page, GDN-2264), fall back to the
      // page-local patient map. When there is no patient ref at all but the
      // converter recorded an intended name, flag it as unlinked.
      const refLabels = (item as { ref_labels?: Record<string, string> })
        .ref_labels;
      const metadata =
        (item.data?.metadata as { source_patient_name?: unknown } | undefined) ??
        {};
      const sourceName =
        typeof metadata.source_patient_name === 'string'
          ? metadata.source_patient_name
          : '';
      let patientLabel: string | undefined;
      let patientUnlinked = false;
      if (item.resource_type !== 'patient') {
        const resolved =
          refLabels?.patient ?? patientNameByKey.get(item.refs?.patient ?? '');
        if (resolved && resolved !== (item.refs?.patient ?? '')) {
          patientLabel = resolved;
        } else if (!item.refs?.patient && sourceName) {
          patientLabel = sourceName;
          patientUnlinked = true;
        }
      }
      const row: ReviewResourceRow = {
        id: item.import_resource_id,
        resourceType: item.resource_type,
        label,
        // The sublabel carries provenance (the source key). Appointments lead
        // with the patient instead, so their detail line is the appointment's
        // own type/date/duration rather than the opaque source key (GDN-2269);
        // when the label already IS the source key, print nothing.
        sublabel:
          item.resource_type === 'appointment'
            ? appointmentDescriptor(item.data)
            : label === item.source_key
              ? undefined
              : item.source_key,
        verdict,
        matchedSummary: detail?.matched_summary ?? undefined,
        competing: detail?.competing?.map(toDiff),
        autoMerge: detail?.auto_merge?.map(toDiff),
        matchCandidates:
          verdict === 'ambiguous'
            ? candidatesFor(
                item.source_key,
                (
                  item as {
                    match_candidates?: {
                      patient_id: string;
                      confidence: number;
                      reason: string;
                    }[];
                  }
                ).match_candidates,
              )
            : undefined,
        matchBasis: verdict === 'ambiguous' ? basisFor(item.source_key) : undefined,
        data: item.data,
        patientKey:
          item.resource_type === 'patient' ? item.source_key : undefined,
        patientLabel,
        patientUnlinked: patientUnlinked || undefined,
      };
      return [row];
    });
    // Surface every conflict, even ones whose staged row fell outside the browse
    // window: synthesize a row from the conflict detail (which carries the label,
    // matched summary, and competing/auto-merge fields needed to decide it). The
    // raw payload isn't in the conflict detail, so `data` is left off for these.
    const shownIds = new Set(windowedRows.map((r) => r.id));
    const extraConflictRows = conflictItems
      .filter((c) => !shownIds.has(c.import_resource_id))
      .map<ReviewResourceRow>((c) => ({
        id: c.import_resource_id,
        resourceType: c.resource_type,
        label: c.title ?? c.source_key,
        sublabel: c.title ? c.source_key : undefined,
        verdict: 'conflict',
        matchedSummary: c.matched_summary ?? undefined,
        competing: c.competing?.map(toDiff),
        autoMerge: c.auto_merge?.map(toDiff),
        pinned: true,
        patientKey: c.resource_type === 'patient' ? c.source_key : undefined,
      }));
    // Same for ambiguous patients: the match feed is paged to completion, so
    // synthesize a row for any ambiguous patient whose staged row fell outside
    // the browse window (the feed carries name/dob for the label and the
    // candidate set). The row id is the patient source key — the resolution
    // plumbing resolves it back to the same `resolutions` entry.
    const shownPatientKeys = new Set(
      items
        .filter((i) => i.resource_type === 'patient')
        .map((i) => i.source_key),
    );
    const extraAmbiguousRows = [...matchByKey.entries()]
      .filter(
        ([key, m]) => m.status === 'ambiguous' && !shownPatientKeys.has(key),
      )
      .map<ReviewResourceRow>(([key, m]) => {
        const label = [m.name, m.dob].filter(Boolean).join(' · ') || key;
        return {
          id: key,
          resourceType: 'patient',
          label,
          sublabel: label === key ? undefined : key,
          verdict: 'ambiguous',
          matchCandidates: candidatesFor(key, undefined),
          matchBasis: basisFor(key),
          pinned: true,
          patientKey: key,
        };
      });
    return [...windowedRows, ...extraConflictRows, ...extraAmbiguousRows];
  }, [allRowsData, conflicts, matchByKey]);

  // Ambiguous patient rows carry their resolution through the same
  // `resolutions` map (keyed by patient source key) the patient picker uses —
  // one source of truth, both UIs stay in sync. The browser speaks row ids, so
  // adapt both directions. Beyond-window ambiguous rows are synthesized with
  // the source key AS the row id, so those map to themselves.
  const patientKeyByRowId = useMemo(() => {
    const map = new Map(
      (allRowsData?.items ?? [])
        .filter((i) => i.resource_type === 'patient')
        .map((i) => [i.import_resource_id, i.source_key]),
    );
    for (const [key, m] of matchByKey) {
      if (m.status === 'ambiguous' && !map.has(key)) map.set(key, key);
    }
    return map;
  }, [allRowsData, matchByKey]);
  const browserMatchResolutions = useMemo(() => {
    const out: Record<string, ReviewMatchResolution> = {};
    for (const [rowId, key] of patientKeyByRowId) {
      const r = resolutions.get(key);
      if (!r) continue;
      out[rowId] =
        r.action === 'link' && r.target_patient_id
          ? { action: 'link', targetPatientId: r.target_patient_id }
          : { action: 'create' };
    }
    return out;
  }, [patientKeyByRowId, resolutions]);
  const handleBrowserMatchResolution = useCallback(
    (rowId: string, resolution: ReviewMatchResolution | null) => {
      const key = patientKeyByRowId.get(rowId);
      if (!key) return;
      setResolution(
        key,
        resolution == null
          ? null
          : resolution.action === 'link'
            ? { action: 'link', target_patient_id: resolution.targetPatientId }
            : { action: 'create' },
      );
    },
    [patientKeyByRowId, setResolution],
  );

  const countsByType = summary?.counts_by_resource_type ?? {};
  const resourceTypes = Object.keys(countsByType).sort();
  const items = list?.items ?? [];
  const total = list?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const refresh = () => {
    void refetchSummary();
    void refetchList();
  };

  // The converter provenance recorded at staging time (GDN-1978): the
  // _artifacts/ prefix, the artifact object keys, and the convert.py hash.
  const artifactsPrefix = summary?.artifacts_prefix ?? undefined;
  const artifactKeys = summary?.artifact_keys ?? [];
  const converterSha256 = summary?.converter_sha256 ?? undefined;
  const hasProvenance = Boolean(
    artifactsPrefix || converterSha256 || artifactKeys.length,
  );

  // If the loader stashed `_artifacts/provider_mapping.json` with the batch, read
  // it so the provider-mapping step can offer a one-click pre-fill instead of
  // resolving each provider by hand. Best-effort: any failure just hides the
  // button. Mirrors ArtifactViewerModal's content fetch.
  const mappingArtifactKey = useMemo(
    () =>
      (summary?.artifact_keys ?? []).find(
        k => artifactName(k, artifactsPrefix) === 'provider_mapping.json',
      ),
    [summary?.artifact_keys, artifactsPrefix],
  );
  const [suggestedMapping, setSuggestedMapping] = useState<
    Record<string, string> | undefined
  >(undefined);

  useEffect(() => {
    if (!showCommit || !mappingArtifactKey) {
      setSuggestedMapping(undefined);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAuth0AccessTokenSilently();
        const params = new URLSearchParams({
          organization_id: organizationId,
          key: mappingArtifactKey,
        });
        const res = await fetch(
          `${API_BASE_URL}/v2/admin/data-migration/sessions/${sessionId}/artifacts/content?${params}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return;
        const data = (await res.json()) as { text?: string };
        const parsed: unknown = data.text ? JSON.parse(data.text) : undefined;
        if (
          !cancelled &&
          parsed &&
          typeof parsed === 'object' &&
          !Array.isArray(parsed)
        ) {
          setSuggestedMapping(parsed as Record<string, string>);
        }
      } catch {
        // best-effort; no pre-fill button if the artifact can't be read/parsed
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCommit, mappingArtifactKey, organizationId, sessionId]);

  // Open a single artifact via a short-lived presigned URL. The backend only
  // signs keys recorded on this session, so an arbitrary key can't be fetched.
  // Fetched fresh each time (not via cached RTK query) since the URL expires.
  const openArtifact = async (key: string) => {
    try {
      const token = await getAuth0AccessTokenSilently();
      const params = new URLSearchParams({
        organization_id: organizationId,
        key,
      });
      const res = await fetch(
        `${API_BASE_URL}/v2/admin/data-migration/sessions/${sessionId}/artifacts/download?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        throw new Error(`Download failed (${res.status})`);
      }
      const data = (await res.json()) as { url: string };
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch {
      enqueueSnackbar('Could not open artifact.', { variant: 'error' });
    }
  };

  // Type scoping now lives on the commit board (per-type Commit buttons,
  // GDN-2253); the header button always commits everything still staged.
  const selectableTypes = resourceTypes.filter(
    rt => (countsByType[rt]?.staged ?? 0) > 0,
  );
  const selectedTypes = selectableTypes;
  const selectedStagedCount = selectedTypes.reduce(
    (acc, rt) => acc + (countsByType[rt]?.staged ?? 0),
    0,
  );
  const allSelected = selectableTypes.length > 0;

  // Provider mapping gate: encounters commit owned by their source provider,
  // so a required (encounter-referenced) provider must be mapped before the
  // commit can include encounters — else those rows block server-side.
  const { data: sourceProviders } = useListSourceProvidersQuery(
    { organizationId, sessionId },
    { skip: !showCommit },
  );
  const providers = sourceProviders?.items ?? [];
  const encounterSelected = selectedTypes.includes('encounter');
  // Required (encounter/note-referenced) providers still unmapped. Computed
  // unconditionally so the provider board row — and every place that reports
  // "N to map" — shows the true figure even when encounters aren't in the
  // current selection (a provider-only staged batch, or notes referencing
  // providers with no encounter type). GDN-2256.
  const unmappedRequiredProviders = providers.filter(
    p => p.required && !providerMapping[p.provider_key],
  );
  // The encounter commit hard-requires its referenced providers be mapped, so
  // that gate only bites when encounters are actually in the selection.
  const unmappedRequired = encounterSelected ? unmappedRequiredProviders : [];
  const providerGated = unmappedRequired.length > 0;


  // A failed query must read as a failure, never as an empty session: on a
  // 120k-row import every one of these timed out and the page claimed "No
  // staged resources for this session yet" (GDN-2247).
  const retryFor = (refetch: () => unknown) => () => {
    void refetch();
  };
  const loadFailures = [
    summaryError && { label: 'summary', retry: retryFor(refetchSummary) },
    classifySummaryError && {
      label: 'review verdicts',
      retry: retryFor(refetchClassifySummary),
    },
    conflictsError && { label: 'conflicts', retry: retryFor(refetchConflicts) },
    browseError && {
      label: 'staged records',
      retry: retryFor(refetchBrowse),
    },
    matchesError && {
      label: 'patient matches',
      retry: retryFor(refetchMatches),
    },
  ].filter((f): f is { label: string; retry: () => void } => Boolean(f));
  // An ambiguous patient must be resolved before the whole-session commit;
  // scoped row/cohort commits are gated server-side against their own
  // selection (GDN-2250). The match feed covers every staged patient.
  const unresolvedAmbiguous = selectedTypes.includes('patient')
    ? [...matchByKey.entries()]
        .filter(([k, m]) => m.status === 'ambiguous' && !resolutions.has(k))
        .map(([k]) => k)
    : [];
  // Complete per-verdict patient cohorts for filter-scoped commits
  // (GDN-2251): from the fully-paged match + conflict feeds, never the browse
  // window. 'matched' patients auto-link; they commit with the whole type.
  const patientKeysByVerdict = useMemo(() => {
    const cohorts: Partial<Record<ImportVerdict, string[]>> = {
      new: [],
      ambiguous: [],
      conflict: [],
    };
    for (const [key, m] of matchByKey) {
      if (m.status === 'new') cohorts.new?.push(key);
      else if (m.status === 'ambiguous') cohorts.ambiguous?.push(key);
    }
    for (const c of conflicts?.items ?? []) {
      if (c.resource_type === 'patient') cohorts.conflict?.push(c.source_key);
    }
    return cohorts;
  }, [matchByKey, conflicts]);

  // Commit board rows (GDN-2253): ingest counts + what still needs a human,
  // per type. Open decisions come from the fully-paged conflict feed plus,
  // for patients, unresolved ambiguous matches.
  const boardRows = resourceTypes.map(rt => {
    const c = countsByType[rt] ?? {};
    const openConflictsOfType = (conflicts?.items ?? []).filter(
      x => x.resource_type === rt && !decisions[x.import_resource_id],
    ).length;
    const openMatchesOfType = rt === 'patient' ? unresolvedAmbiguous.length : 0;
    return {
      resourceType: rt,
      staged: c.staged ?? 0,
      committed: c.committed ?? 0,
      skipped: c.skipped ?? 0,
      invalid: c.invalid ?? 0,
      openDecisions: openConflictsOfType + openMatchesOfType,
      unmappedProviders:
        rt === 'encounter'
          ? unmappedRequired.length
          : rt === 'provider'
            ? unmappedRequiredProviders.length
            : undefined,
    };
  });

  const totalOpenDecisions = boardRows.reduce(
    (acc, r) => acc + r.openDecisions,
    0,
  );

  // Every conflicting row of a type being committed must carry a decision: the
  // backend blocks the run otherwise (no auto-escalation). Scope mirrors the
  // backend gate — by resource type, not the patient picker. Computed from the
  // fully-loaded conflict set (paged to completion), so it's complete regardless
  // of the browse window; conflictsPending (below) covers the load-in-flight gap.
  const unresolvedConflicts = (conflicts?.items ?? []).filter(
    (c) =>
      selectedTypes.includes(c.resource_type) &&
      !decisions[c.import_resource_id],
  );
  // Until the conflict fetch resolves we can't see the conflicts yet, so a
  // premature commit would be rejected server-side. Gate while it's in flight.
  const conflictsPending = hasMatchedChanges && (conflictsLoading || !conflicts);

  // Gate while match results are still loading: until they resolve, matchByKey
  // is empty so unresolvedAmbiguous can't see ambiguous patients yet.
  const commitGated =
    providerGated ||
    matchesLoading ||
    unresolvedAmbiguous.length > 0 ||
    matchesIncomplete ||
    conflictsPending ||
    conflictsIncomplete ||
    unresolvedConflicts.length > 0;

  const isFullCommit = allSelected;
  const scopedStagedCount = selectedStagedCount;

  const [commitStagedResources] = useCommitStagedResourcesMutation();
  const [requestClassify, { isLoading: reclassifying }] =
    useRequestClassifyMutation();
  const handleReclassify = async () => {
    try {
      await requestClassify({ organizationId, sessionId }).unwrap();
      enqueueSnackbar(
        'Re-classify started — verdicts refresh in about a minute.',
        { variant: 'info' },
      );
    } catch {
      enqueueSnackbar('Failed to start re-classify.', { variant: 'error' });
    }
  };
  // Whether the operation being tracked committed the whole selection (the
  // header Commit button) or a patient-scoped slice (row/cohort commit). One
  // commit runs at a time (`committing` gates every entry point), so a single
  // flag is enough.
  const scopedCommitRef = useRef(false);
  const operationTracking = useImportOperationTracking({
    sessionId,
    onOperationComplete: operation => {
      setCommitting(false);
      if (!scopedCommitRef.current) {
        // After a full commit, reset so the next run defaults back to "all
        // staged": every type, no carried-over provider mapping or decisions.
        setProviderMapping({});
        setResolutions(new Map());
        setDecisions({});
      }
      // After a scoped (row/cohort) commit, every other in-progress decision
      // stays: the whole point is committing slices while the rest of the
      // queue is still being worked. Entries for the rows that just committed
      // are harmless leftovers — committed rows leave the staged queue and
      // the engine skips them idempotently.
      enqueueSnackbar(
        `Commit complete: ${operation.progress?.imported_count ?? 0} committed, ` +
          `${operation.progress?.failed_count ?? 0} failed, ` +
          `${operation.progress?.skipped_count ?? 0} skipped.`,
        { variant: 'success' },
      );
      refresh();
    },
    onOperationError: operation => {
      setCommitting(false);
      enqueueSnackbar(`Commit failed: ${operation.error ?? 'Unknown error'}`, {
        variant: 'error',
      });
      refresh();
    },
  });

  const handleCommit = async () => {
    setConfirmOpen(false);
    setCommitting(true);
    scopedCommitRef.current = false;
    try {
      const result = await commitStagedResources({
        sessionId,
        commitStagedResourcesRequest: {
          organization_id: organizationId,
          // Send the explicit subset; all-selected == commit everything.
          resource_types: selectedTypes,
          // Source provider -> platform user; encounters land owned by the
          // mapped clinician. Unmapped required providers are gated before here.
          provider_mapping: providerMapping,
          // Operator match decisions; omit when none so auto-matching applies.
          patient_resolutions:
            resolutions.size > 0
              ? Object.fromEntries(resolutions)
              : undefined,
          // Operator conflict decisions, keyed by import_resource_id; omit when
          // none. The commit button is gated until every in-scope conflict is
          // decided, so by here these cover them.
          conflict_resolutions:
            Object.keys(decisions).length > 0 ? decisions : undefined,
        },
      }).unwrap();
      if (result.operation_id) {
        operationTracking.startTracking(result.operation_id);
      } else {
        setCommitting(false);
        refresh();
      }
    } catch (error: unknown) {
      setCommitting(false);
      const message =
        (error as { data?: { message?: string; error_code?: string } })?.data
          ?.message ?? 'Failed to dispatch commit.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Progressive-trust commit (GDN-2250/2251): commit the given patients'
  // RECORDS only (children come later via type-scoped runs). The server
  // re-gates against exactly this selection, so conflicts outside it don't
  // block, and a premature commit of an undecided patient blocks cleanly.
  const commitPatients = async (patientKeys: string[]) => {
    setCommitting(true);
    scopedCommitRef.current = true;
    try {
      const result = await commitStagedResources({
        sessionId,
        commitStagedResourcesRequest: {
          organization_id: organizationId,
          resource_types: ['patient'],
          patient_keys: patientKeys,
          patient_resolutions:
            resolutions.size > 0
              ? Object.fromEntries(resolutions)
              : undefined,
          conflict_resolutions:
            Object.keys(decisions).length > 0 ? decisions : undefined,
        },
      }).unwrap();
      if (result.operation_id) {
        operationTracking.startTracking(result.operation_id);
      } else {
        setCommitting(false);
        refresh();
      }
    } catch (error: unknown) {
      setCommitting(false);
      const message =
        (error as { data?: { message?: string; error_code?: string } })?.data
          ?.message ?? 'Failed to dispatch commit.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Type-wave commit (GDN-2253): one type, everything staged in it. The
  // board only enables this once the type's own gates and its parent wave
  // pass, and the server re-checks everything regardless.
  const commitTypes = async (resourceTypeToCommit: string) => {
    setCommitting(true);
    scopedCommitRef.current = true;
    try {
      const result = await commitStagedResources({
        sessionId,
        commitStagedResourcesRequest: {
          organization_id: organizationId,
          resource_types: [resourceTypeToCommit],
          provider_mapping: providerMapping,
          patient_resolutions:
            resolutions.size > 0
              ? Object.fromEntries(resolutions)
              : undefined,
          conflict_resolutions:
            Object.keys(decisions).length > 0 ? decisions : undefined,
        },
      }).unwrap();
      if (result.operation_id) {
        operationTracking.startTracking(result.operation_id);
      } else {
        setCommitting(false);
        refresh();
      }
    } catch (error: unknown) {
      setCommitting(false);
      const message =
        (error as { data?: { message?: string; error_code?: string } })?.data
          ?.message ?? 'Failed to dispatch commit.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Staged Resources"
        subheader={
          summary
            ? `Session ${summary.status} · ${total} resource(s)` +
              (summary.s3_prefix ? ` · ${summary.s3_prefix}` : '')
            : undefined
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showCommit && (
              <Tooltip
                title={
                  matchesLoading || conflictsPending
                    ? 'Loading review results…'
                    : matchesIncomplete
                      ? 'Too many patients to load match results at once — commit is disabled to avoid missing an ambiguous match'
                      : conflictsIncomplete
                        ? 'Too many conflicts to load at once — commit is disabled to avoid missing a required decision'
                        : unresolvedAmbiguous.length > 0
                          ? `Resolve ${unresolvedAmbiguous.length} ambiguous patient match(es) before committing`
                          : unresolvedConflicts.length > 0
                            ? `Resolve ${unresolvedConflicts.length} conflict(s) before committing`
                            : providerGated
                              ? 'Map all providers with encounters before committing'
                              : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={
                      committing ? <CircularProgress size={16} /> : <Publish />
                    }
                    disabled={
                      committing || scopedStagedCount === 0 || commitGated
                    }
                    onClick={() => setConfirmOpen(true)}
                  >
                    {committing
                      ? 'Committing…'
                      : isFullCommit
                        ? `Commit staged (${scopedStagedCount})`
                        : `Commit selected (${scopedStagedCount})`}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Re-run the classify pass — use after a matching/classify change so verdicts reflect current behavior. Async; refresh after a minute.">
              <span>
                <Button
                  variant="outlined"
                  disabled={reclassifying}
                  onClick={() => void handleReclassify()}
                >
                  Re-run classify
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Batch already converted — adding files is locked. Start a new batch instead.">
              <span>
                <Button variant="outlined" disabled>
                  Add files
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                onClick={refresh}
                aria-label="Refresh staged resources"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        {loadFailures.length > 0 && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => loadFailures.forEach(f => f.retry())}
              >
                Retry
              </Button>
            }
          >
            Couldn&apos;t load {loadFailures.map(f => f.label).join(', ')} for
            this session — the request failed or timed out. The counts and
            lists below may be missing or incomplete; the staged data itself
            is not affected.
          </Alert>
        )}
        {committing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Commit running in the background — staged resources are being
            written to the live tables. This view refreshes when it finishes.
          </Alert>
        )}
        {openType === PROVIDERS_SURFACE && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Button size="small" onClick={() => setOpenType(null)}>
                ← Overview
              </Button>
              <Typography variant="h6">Source providers</Typography>
              <Typography variant="body2" color="text.secondary">
                {unmappedRequiredProviders.length > 0
                  ? `${unmappedRequiredProviders.length} to map`
                  : 'all mapped'}
              </Typography>
            </Box>
            <ProviderMappingPanel
              organizationId={organizationId}
              providers={providers}
              value={providerMapping}
              onChange={setProviderMapping}
              encounterSelected={encounterSelected}
              suggestedMapping={suggestedMapping}
            />
          </Box>
        )}
        {openType !== null && openType !== PROVIDERS_SURFACE && hasClassifyVerdicts && classifySummary && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Button size="small" onClick={() => setOpenType(null)}>
                ← Overview
              </Button>
              <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                {browseType === 'all'
                  ? 'All types'
                  : browseType.replaceAll('_', ' ')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(browseType === 'all'
                  ? Object.values(countsByType).reduce(
                      (acc, c) => acc + (c.staged ?? 0),
                      0,
                    )
                  : countsByType[browseType]?.staged ?? 0
                ).toLocaleString()}{' '}
                staged
              </Typography>
            </Box>
            {(conflictsIncomplete || matchesIncomplete) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This import is unusually large and some conflict or patient-match
                data couldn&apos;t be fully loaded for review. Committing is
                disabled to avoid missing a required decision — contact support.
              </Alert>
            )}
            {!conflictsIncomplete && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Browse, filters, and search cover the whole import.{' '}
                {totalConflictCount > 0
                  ? `All ${totalConflictCount.toLocaleString()} conflict(s) are loaded below and must be resolved before committing. `
                  : 'This import has no conflicts to review. '}
                {autoLinkCount > 0 &&
                  `${autoLinkCount.toLocaleString()} patient(s) will link to existing records — verify the matches below before committing.`}
              </Alert>
            )}
            <ImportReviewBrowser
              sourceLabel={classifySummary.source_label ?? undefined}
              totalRecords={scopedTotalRecords || reviewRows.length}
              totals={browserTotals}
              rows={reviewRows}
              decisions={decisions}
              onDecisionChange={(id, decision) =>
                setDecisions((prev) => {
                  if (decision) return { ...prev, [id]: decision };
                  const { [id]: _removed, ...rest } = prev;
                  return rest;
                })
              }
              matchResolutions={browserMatchResolutions}
              onMatchResolutionChange={handleBrowserMatchResolution}
              serverBrowse={{
                typeFilter: browseType,
                onTypeFilterChange: setBrowseType,
                search: browseSearch,
                onSearchChange: setBrowseSearch,
                page: browsePage,
                onPageChange: setBrowsePage,
                perPage: BROWSE_PAGE_SIZE,
                totalRows: browseTotalRows,
                resourceTypes: Object.keys(
                  summary?.counts_by_resource_type ?? {},
                ).sort(),
              }}
              commit={
                showCommit
                  ? {
                      onCommitPatients: (keys) => void commitPatients(keys),
                      inFlight: committing,
                      patientKeysByVerdict,
                    }
                  : undefined
              }
              onViewDocument={(id) => setDocViewId(id)}
              showCommitBar={false}
              provenance={
                hasProvenance ? (
                  <Box sx={{ fontSize: 13 }}>
                    {converterSha256 && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                          mb: 0.5,
                        }}
                      >
                        convert.py SHA-256 · {converterSha256}
                      </Typography>
                    )}
                    {artifactKeys.map((key) => (
                      <Typography
                        key={key}
                        variant="caption"
                        onClick={() => setViewerKey(key)}
                        sx={{
                          display: 'block',
                          color: 'primary.main',
                          cursor: 'pointer',
                          fontFamily: 'monospace',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {artifactName(key, artifactsPrefix)}
                      </Typography>
                    ))}
                  </Box>
                ) : undefined
              }
            />
          </Box>
        )}
        {openType === null && (
          <>
        {showCommit && hasClassifyVerdicts && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {totalOpenDecisions > 0 || unmappedRequiredProviders.length > 0
              ? `${totalOpenDecisions.toLocaleString()} decision${
                  totalOpenDecisions === 1 ? '' : 's'
                } and ${unmappedRequiredProviders.length} provider mapping${
                  unmappedRequiredProviders.length === 1 ? '' : 's'
                } stand between this import and its next commit. Click a row to review; commit types top to bottom.`
              : 'Nothing needs a decision — commit types top to bottom, or everything at once.'}
          </Alert>
        )}
        {summaryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : resourceTypes.length === 0 ? (
          summaryError ? (
            <Typography variant="body2" color="error">
              The staged-resource summary couldn&apos;t be loaded — use Retry
              above.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No staged resources for this session yet.
            </Typography>
          )
        ) : showCommit ? (
          <CommitBoard
            rows={boardRows}
            providers={
              // With staged provider envelopes the provider TYPE row carries
              // the mapping task; the synthetic row is the fallback for
              // batches whose converter emitted none (GDN-2256).
              providers.length > 0 && !resourceTypes.includes('provider')
                ? {
                    total: providers.length,
                    unmapped: unmappedRequiredProviders.length,
                  }
                : undefined
            }
            onOpenProviders={() => setOpenType(PROVIDERS_SURFACE)}
            waitsFor={
              (summary as { waits_for?: Record<string, string[]> } | undefined)
                ?.waits_for ?? {}
            }
            onCommitType={rt => void commitTypes(rt)}
            onOpenType={rt =>
              rt === 'provider' ? setOpenType(PROVIDERS_SURFACE) : openReview(rt)
            }
            commitInFlight={committing}
          />
        ) : (
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Resource type</TableCell>
                {STATUSES.map(s => (
                  <TableCell key={s} align="right">
                    {s}
                  </TableCell>
                ))}
                <TableCell align="right">total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resourceTypes.map(rt => {
                const row = countsByType[rt] ?? {};
                const rowTotal = Object.values(row).reduce((a, b) => a + b, 0);
                return (
                  <TableRow
                    key={rt}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setResourceType(rt);
                      setPage(1);
                    }}
                  >
                    <TableCell>{rt}</TableCell>
                    {STATUSES.map(s => (
                      <TableCell key={s} align="right">
                        {row[s] ?? 0}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <strong>{rowTotal}</strong>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {hasProvenance && (
          <Box sx={{ mb: 1 }}>
            <Button size="small" onClick={() => setProvenanceOpen(v => !v)}>
              {provenanceOpen ? 'Hide' : 'Show'} provenance & artifacts
            </Button>
          </Box>
        )}
        {hasProvenance && (
          <Collapse in={provenanceOpen} unmountOnExit>
          <Box
            sx={{
              mb: 3,
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Converter provenance
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}
            >
              How this batch was converted, captured when it was staged.
            </Typography>
            {converterSha256 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: artifactsPrefix || artifactKeys.length ? 1 : 0,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 120 }}>
                  convert.py SHA-256
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {converterSha256}
                </Typography>
                <Tooltip title="Copy SHA-256">
                  <IconButton
                    size="small"
                    aria-label="Copy converter SHA-256"
                    onClick={() => copy(converterSha256, 'SHA-256')}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {artifactsPrefix && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: artifactKeys.length ? 1 : 0,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 120 }}>
                  Artifacts prefix
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {artifactsPrefix}
                </Typography>
                <Tooltip title="Copy prefix">
                  <IconButton
                    size="small"
                    aria-label="Copy artifacts prefix"
                    onClick={() => copy(artifactsPrefix, 'Prefix')}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {artifactKeys.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Artifacts
                </Typography>
                {artifactKeys.map((key) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.25,
                    }}
                  >
                    <Typography
                      variant="body2"
                      onClick={() => setViewerKey(key)}
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        wordBreak: 'break-all',
                        flex: 1,
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {artifactName(key, artifactsPrefix)}
                    </Typography>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        aria-label={`View artifact ${key}`}
                        onClick={() => setViewerKey(key)}
                      >
                        <Visibility fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        aria-label={`Download artifact ${key}`}
                        onClick={() => void openArtifact(key)}
                      >
                        <Download fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          </Collapse>
        )}

        {!hasClassifyVerdicts && (
          <>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Resource type</InputLabel>
            <Select
              label="Resource type"
              value={resourceType}
              onChange={e => {
                setResourceType(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {resourceTypes.map(rt => (
                <MenuItem key={rt} value={rt}>
                  {rt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={e => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isFetching && <CircularProgress size={18} />}
        </Box>

        {listLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No resources match the current filters.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Resource type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Committed ID</TableCell>
                <TableCell>Source key</TableCell>
                <TableCell align="right">Errors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => {
                const isOpen = expanded === item.import_resource_id;
                const errs = item.validation_errors ?? [];
                const commitErrs = item.commit_errors ?? [];
                return (
                  <Fragment key={item.import_resource_id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label="Toggle details"
                          onClick={() =>
                            setExpanded(
                              isOpen ? null : item.import_resource_id,
                            )
                          }
                        >
                          {isOpen ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{item.resource_type}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.status}
                          color={STATUS_COLOR[item.status] ?? 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {item.committed_resource_id ? (
                          <Link
                            to={`/admin-v2/resources/${item.resource_type}/${item.committed_resource_id}?organization_id=${organizationId}`}
                          >
                            {item.committed_resource_id}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          wordBreak: 'break-all',
                        }}
                      >
                        {item.source_key}
                      </TableCell>
                      <TableCell align="right">
                        {errs.length > 0 || commitErrs.length > 0 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              justifyContent: 'flex-end',
                            }}
                          >
                            {errs.length > 0 && (
                              <Chip
                                size="small"
                                color="error"
                                label={`${errs.length} validation`}
                              />
                            )}
                            {commitErrs.length > 0 && (
                              <Chip
                                size="small"
                                color="warning"
                                label={`${commitErrs.length} commit`}
                              />
                            )}
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ py: 0, border: isOpen ? undefined : 0 }}
                      >
                        <Collapse in={isOpen} unmountOnExit>
                          <Box sx={{ p: 2 }}>
                            {errs.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="error">
                                  Validation errors
                                </Typography>
                                <pre
                                  style={{
                                    margin: 0,
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {JSON.stringify(errs, null, 2)}
                                </pre>
                              </Box>
                            )}
                            {commitErrs.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="warning.main"
                                >
                                  Commit errors
                                </Typography>
                                <pre
                                  style={{
                                    margin: 0,
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {JSON.stringify(commitErrs, null, 2)}
                                </pre>
                              </Box>
                            )}
                            <Typography variant="subtitle2">Data</Typography>
                            <pre
                              style={{
                                margin: 0,
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                                maxHeight: 320,
                                overflow: 'auto',
                              }}
                            >
                              {JSON.stringify(item.data ?? {}, null, 2)}
                            </pre>
                            {item.refs &&
                              Object.keys(item.refs).length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2">
                                    Refs
                                  </Typography>
                                  <pre style={{ margin: 0, fontSize: 12 }}>
                                    {JSON.stringify(item.refs, null, 2)}
                                  </pre>
                                </Box>
                              )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_e, p) => setPage(p)}
              size="small"
            />
          </Box>
        )}
          </>
        )}
          </>
        )}
      </CardContent>

      <Dialog
        open={docViewId !== null}
        onClose={() => setDocViewId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Source document</DialogTitle>
        <DialogContent>
          {docLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : docError ? (
            <DialogContentText color="error">
              Couldn&apos;t load the document — the row may have no source file.
            </DialogContentText>
          ) : (
            <>
              {docContent?.truncated && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Showing the first 1 MB of {docContent.size.toLocaleString()}{' '}
                  bytes.
                </Alert>
              )}
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  m: 0,
                }}
              >
                {docContent?.text}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocViewId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {isFullCommit ? 'Commit staged resources?' : 'Commit selected?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This writes {scopedStagedCount} staged resource
            {scopedStagedCount === 1 ? '' : 's'}
            {allSelected ? '' : ` (${selectedTypes.join(', ')})`} into the live
            tables for this organization. Rows that fail are
            marked with commit errors and the run continues; already-committed
            rows are skipped, so re-running is safe.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleCommit()}>
            Commit
          </Button>
        </DialogActions>
      </Dialog>

      <ArtifactViewerModal
        open={viewerKey !== null}
        onClose={() => setViewerKey(null)}
        organizationId={organizationId}
        sessionId={sessionId}
        artifactKey={viewerKey}
        displayName={
          viewerKey ? artifactName(viewerKey, artifactsPrefix) : ''
        }
        onDownload={(key) => void openArtifact(key)}
      />
    </Card>
  );
}
